import { FilterOperator, FilterPathType, RiskLevel } from "domain/asset-management/assetFilterConstants";
import {
    AssetPathKey,
    MANDATORY_ASSET_FIELDS,
    SOURCE_FIELD_TO_ASSET_PATH,
} from "domain/asset-management/assetPathMapping";
import {
    AssetErasureHistory,
    AssetErasureHistorySource,
    AssetEventAction,
    Assets,
    GradingAction,
} from "domain/asset-management/assets";
import { AssetExportJobDetail, AssetExportJobDetailDto, toAssetExportJobDetail } from "domain/assetExport";
import {
    AssetRegistrationJobDetail,
    AssetRegistrationJobDetailDto,
    toAssetRegistrationJobDetail,
} from "domain/assetRegistration";
import { toUpliftRule, UpliftRule, UpliftRuleDto } from "domain/upliftRules";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";

export const ASSET_MANAGEMENT_PATH = "/api/v1/asset-management/";
const PARENT_ASSET_ID_KEY = "parent_asset_id";
const PATH_API_V1_ASSETS_REGISTRATION = "/v1/api/reports/register-assets";

type AssetDetailDifference = {
    key_before: string | null;
    key_after: string | null;
    value_before: string | null;
    value_after: string | null;
};
function computeDetailsDiff(
    original: { key: string; value: string }[],
    updated: { key: string; value: string }[]
): AssetDetailDifference[] {
    const originalMap = new Map(original.map((detail) => [detail.key, detail.value]));
    const updatedMap = new Map(updated.map((detail) => [detail.key, detail.value]));
    const differences: AssetDetailDifference[] = [];

    for (const [key] of originalMap) {
        if (!updatedMap.has(key)) {
            differences.push({
                key_before: key,
                key_after: null,
                value_before: originalMap.get(key) ?? null,
                value_after: null,
            });
        }
    }

    for (const [key, value] of updatedMap) {
        if (!originalMap.has(key)) {
            differences.push({ key_before: null, key_after: key, value_before: null, value_after: value });
        } else if (originalMap.get(key) !== value) {
            differences.push({
                key_before: key,
                key_after: key,
                value_before: originalMap.get(key) ?? null,
                value_after: value,
            });
        }
    }

    return differences;
}

export enum DataBearingFilter {
    ALL = "ALL",
    DATA_BEARING = "DATA_BEARING",
    NON_DATA_BEARING = "NON_DATA_BEARING",
}

export interface TrackingSummary {
    trackedCount: number;
    nonDataBearingCount: number;
    trackingLimit: number;
    allDataBearingCount: number;
}

export interface RiskSummary {
    totalLow: number;
    totalMedium: number;
    totalHigh: number;
    total: number;
}

interface RiskSummaryDto {
    total_low: number;
    total_medium: number;
    total_high: number;
    total: number;
}

export interface GradedSummary {
    totalGraded: number;
    totalPending: number;
    totalLocked: number;
}

export interface LockDistribution {
    path: string;
    count: number;
}

interface LockDistributionResponseDto {
    distributions: LockDistribution[];
}

export interface AssetOverviewDto {
    total_assets: number;
    tracked_data_bearing_assets: number;
    non_data_bearing_assets: number;
    // Keys are the asset group names (for example "laptop" or "server") and depend on the assets in the view.
    breakdown: Record<string, AssetBreakdownDto>;
}

export interface AssetBreakdownDto {
    count: number;
    has_data_bearing: boolean;
    has_non_data_bearing: boolean;
}

export interface AssetOverview {
    totalAssets: number;
    trackedDataBearingAssets: number;
    nonDataBearingAssets: number;
    breakdown: AssetDeviceBreakdown[];
}

export interface AssetBreakdown {
    count: number;
    hasDataBearing: boolean;
    hasNonDataBearing: boolean;
}

export interface AssetDeviceBreakdown extends AssetBreakdown {
    deviceType: string;
}

export interface AssetBase {
    uuid: string;
    assetId: string;
    assetType?: string;
    serialNumber?: string;
    assetState?: string;
    riskLevel?: string;
    location?: string;
    assignedTo?: string;
    model?: string;
    manufacturer?: string;
    warrantyStatus?: string;
    lastErasureDate?: string;
    dispositionGroup?: string;
    inventoryId?: string;
    deviceType?: string;
    assetTag?: string;
    checked?: boolean;
    disabled?: boolean;
    detailsTruncated?: boolean;
    isTracked?: boolean;
    isDataBearing?: boolean;
    assetDetails?: { key: string; value: string }[];
    groupUuids?: string[];
    parentUuid?: string;
    hasChildren?: boolean;
    hasHiddenChildren?: boolean;
    children?: Asset[];
    hasErasureReport?: boolean;
    hasSuccessfulErasure?: boolean;
    lockState?: string;
    potentialUpliftRules?: UpliftRule[];
    appliedUpliftRules?: UpliftRule[];
    potentialUpliftValue?: number;
}

export type AssetFieldValue =
    string | string[] | boolean | number | { key: string; value: string }[] | Asset[] | UpliftRule[] | undefined;

export type Asset = AssetBase & Record<string, AssetFieldValue>;

export interface AssetFilterDto {
    name: string;
    value: string;
    operator: FilterOperator;
    path_type?: FilterPathType;
}

export interface AssetFilter {
    name: string;
    value: string;
    operator: FilterOperator;
    pathType?: FilterPathType;
}

export interface AssetSearchRequestDto {
    cursor?: string;
    paths: string[];
    size?: number;
    sort_ascending?: boolean;
    sort_column?: string;
    sort_column_type?: FilterPathType;
    search?: string;
    start_date?: string;
    end_date?: string;
    filters?: AssetFilterDto[];
    filter_logic?: string;
    quick_filters?: AssetFilterDto[];
    page_number?: number;
    data_bearing_filter?: DataBearingFilter;
    group_uuid?: string;
    parent_asset_uuid?: string;
}

export interface AssetSearchRequest {
    cursor?: string;
    paths: string[];
    size?: number;
    sortAscending?: boolean;
    sortColumn?: string;
    sortColumnType?: FilterPathType;
    search?: string;
    startDate?: string;
    endDate?: string;
    filters?: AssetFilter[];
    filterLogic?: string;
    quickFilters?: AssetFilter[];
    pageNumber?: number;
    dataBearingFilter?: DataBearingFilter;
    groupUuid?: string;
    parentAssetUuid?: string;
}

interface AssetDistributionRequestDto {
    filters: AssetFilterDto[];
    view_request?: AssetSearchRequestDto;
}

export interface AssetDistributionRequest {
    filters: AssetFilter[];
    viewRequest?: AssetSearchRequest;
}

export interface AssetSearchResponse {
    assets: Asset[];
    cursor?: string;
    currentPage: number;
    totalPages: number;
    totalAssets?: number;
    trackingSummary?: TrackingSummary;
    assetTypes: string[];
}

export interface AssetFilters {
    riskLevel?: string[];
    region?: string;
    dispositionGroup?: string;
    inventoryId?: string;
    deviceType?: string;
}

export interface AssetColumn {
    key: string;
    visible: boolean;
    apiPath: string;
}

const COLUMN_TO_API_PATH: Record<string, string> = {
    assetId: "asset_id",
    assetType: "asset_type",
    assignedTo: "assigned_to",
    serialNumber: "serial_number",
    assetState: "asset_state",
    riskLevel: "risk_level",
    location: "location",
    model: "model",
    manufacturer: "manufacturer",
    warrantyStatus: "warranty_status",
    lastErasureDate: "last_erasure_date",
    dispositionGroup: "disposition_group",
    inventoryId: "inventory_id",
    deviceType: "device_type",
    assetTag: "asset_tag",
};

export function mapColumnsToApiPaths(columns: string[]): string[] {
    return columns.map((assetColumn) => COLUMN_TO_API_PATH[assetColumn] || assetColumn);
}

export function toAssetFilterDto(filter: AssetFilter): AssetFilterDto {
    return {
        name: filter.name,
        value: filter.value,
        operator: filter.operator,
        path_type: filter.pathType,
    };
}

export function toAssetSearchRequestDto(request: AssetSearchRequest): AssetSearchRequestDto {
    return {
        cursor: request.cursor,
        paths: request.paths,
        size: request.size,
        sort_ascending: request.sortAscending,
        sort_column: request.sortColumn,
        sort_column_type: request.sortColumnType,
        search: request.search,
        start_date: request.startDate,
        end_date: request.endDate,
        filters: request.filters?.map(toAssetFilterDto),
        filter_logic: request.filterLogic,
        quick_filters: request.quickFilters?.map(toAssetFilterDto),
        page_number: request.pageNumber,
        data_bearing_filter: request.dataBearingFilter,
        group_uuid: request.groupUuid,
        parent_asset_uuid: request.parentAssetUuid,
    };
}

export function toAssetDistributionRequestDto(request: AssetDistributionRequest): AssetDistributionRequestDto {
    return {
        filters: request.filters.map(toAssetFilterDto),
        view_request: request.viewRequest === undefined ? undefined : toAssetSearchRequestDto(request.viewRequest),
    };
}

export function parseAssetRegistrationJobId(data: string): JobId {
    return toJobId(JSON.parse(data));
}

interface TrackingSummaryDto {
    tracked_count: number;
    non_data_bearing_count: number;
    tracking_limit: number;
    all_data_bearing_count: number;
}

interface AssetSearchResponseDto {
    assets: Record<string, string>[];
    cursor?: string;
    current_page: number;
    total_pages: number;
    total_assets?: number;
    tracking_summary?: TrackingSummaryDto;
    asset_types?: string[];
}

interface JobIdDto {
    job_uuid: string;
}

interface JobId {
    jobUuid: string;
}

function toJobId(dto: JobIdDto): JobId {
    return { jobUuid: dto.job_uuid };
}

export interface AssetsDto {
    asset_id: string;
    serial_number: string;
    asset_tag: string;
    asset_type: string;
    asset_state: string;
    manufacturer: string;
    assigned_to: string;
    location: string;
    department: string;
    region: string;
    parent_asset_id?: string;
    asset_details: {
        key: string;
        value: string;
        source: string;
    }[];
}

export interface AssetKeys {
    keys: string[];
}

export function trimAssets(asset: Assets): Assets {
    return {
        assetId: asset.assetId.trim(),
        serialNumber: asset.serialNumber.trim(),
        assetType: asset.assetType.trim(),
        assetTag: asset.assetTag.trim(),
        manufacturer: asset.manufacturer.trim(),
        model: asset.model.trim(),
        assetState: asset.assetState.trim(),
        assignedTo: asset.assignedTo.trim(),
        location: asset.location.trim(),
        department: asset.department.trim(),
        region: asset.region.trim(),
        assetDetails: (asset.assetDetails ?? []).map((detail) => ({
            key: detail.key.trim(),
            value: detail.value.trim(),
        })),
    };
}

export function toCreateAsset(untrimmedAsset: Assets): AssetsDto {
    const asset = trimAssets(untrimmedAsset);
    const parentAssetIdDetail = (asset.assetDetails ?? []).find((detail) => detail.key === PARENT_ASSET_ID_KEY);
    return {
        asset_id: asset.assetId,
        serial_number: asset.serialNumber,
        asset_tag: asset.assetTag,
        asset_type: asset.assetType,
        manufacturer: asset.manufacturer,
        assigned_to: asset.assignedTo,
        asset_state: asset.assetState,
        location: asset.location,
        department: asset.department,
        region: asset.region,
        parent_asset_id: parentAssetIdDetail?.value,
        asset_details: [
            { key: "serial_number", value: asset.serialNumber, source: "manual" },
            ...(asset.assetDetails ?? [])
                .filter((detail) => detail.key !== "serial_number")
                .map((detail) => ({
                    key: detail.key,
                    value: detail.value,
                    source: "manual",
                })),
        ],
    };
}

function toEditAssets(asset: Assets): { key: string; value: string }[] {
    const excludedKeys = new Set<string>([
        ...MANDATORY_ASSET_FIELDS,
        "asset_tag",
        "manufacturer",
        "assigned_to",
        "asset_state",
        "location",
        "department",
        "region",
        "model",
        PARENT_ASSET_ID_KEY,
    ]);
    return [
        { key: "serial_number", value: asset.serialNumber },
        { key: "asset_tag", value: asset.assetTag },
        { key: "asset_type", value: asset.assetType },
        { key: "manufacturer", value: asset.manufacturer },
        { key: "model", value: asset.model },
        { key: "assigned_to", value: asset.assignedTo },
        { key: "asset_state", value: asset.assetState },
        { key: "location", value: asset.location },
        { key: "department", value: asset.department },
        { key: "region", value: asset.region },
        ...asset.assetDetails.filter((detail) => !excludedKeys.has(detail.key)),
    ];
}

interface AssetEventDto {
    action: AssetEventAction;
    event_date: string;
    key_before: string;
    key_after: string;
    value_before: string;
    value_after: string;
    source: string;
    edited_by: string;
}

export interface AssetEvent {
    action: AssetEventAction;
    eventDate: string;
    keyBefore: string;
    keyAfter: string;
    valueBefore: string;
    valueAfter: string;
    source: string;
    editedBy: string;
}

function toAssetEvent(dto: AssetEventDto): AssetEvent {
    return {
        action: dto.action,
        editedBy: dto.edited_by,
        eventDate: dto.event_date,
        keyAfter: dto.key_after,
        keyBefore: dto.key_before,
        source: dto.source,
        valueAfter: dto.value_after,
        valueBefore: dto.value_before,
    };
}

function toAssetEventsResponse(dto: AssetEventResponseDto): AssetEventResponse {
    return {
        events: dto.events.map((each) => toAssetEvent(each)),
        creationDate: dto.creation_date,
    };
}

interface AssetEventResponseDto {
    events: AssetEventDto[];
    creation_date: string;
}

interface AssetEventResponse {
    events: AssetEvent[];
    creationDate: string;
}

export interface ExportEmailRequestDto {
    to: string;
    subject: string;
}

export interface BulkDeleteAssetsRequest {
    asset_uuids: string[];
}

export interface BulkDeleteAssetsResponse {
    failedAssetUuids: string[];
    deletedCount?: number;
}

export interface BulkDeleteAssetsResponseDto {
    failed_asset_uuids: string[];
}

export interface BulkValueRecoveryActionResponse {
    failedAssetUuids: string[];
}

interface BulkValueRecoveryActionRequest {
    action: string;
    assets: string[];
}

interface BulkValueRecoveryActionResponseDto {
    failed_asset_uuids: string[];
}

interface AssetErasureHistoryDto {
    report_uuid: string;
    erasure_date: string;
    erasure_status: string;
    source: AssetErasureHistorySource;
}

interface AssetErasureHistoryResponseDto {
    history: AssetErasureHistoryDto[];
}

function toAssetErasureHistory(dto: AssetErasureHistoryDto): AssetErasureHistory {
    return {
        erasureDate: dto.erasure_date,
        erasureStatus: dto.erasure_status.toUpperCase(),
        reportUuid: dto.report_uuid,
        source: dto.source,
    };
}
function isAssetScalarValue(value: unknown): value is string | number {
    return typeof value === "string" || typeof value === "number";
}

export function toAssetStringValue(value: unknown): string | undefined {
    if (!isAssetScalarValue(value)) {
        return undefined;
    }
    const stringValue = value.toString();
    if (stringValue.length === 0 || stringValue === "null") {
        return undefined;
    }
    return stringValue;
}

export class AssetService {
    private static readonly ASSET_PATH_TO_SOURCE_FIELDS = AssetService.buildAssetPathToSourceFields();

    private static buildAssetPathToSourceFields(): Map<AssetPathKey, string[]> {
        return Object.entries(SOURCE_FIELD_TO_ASSET_PATH).reduce((map, [sourceField, pathKey]) => {
            const existing = map.get(pathKey) ?? [];
            map.set(pathKey, [...existing, sourceField]);
            return map;
        }, new Map<AssetPathKey, string[]>());
    }

    private mapAssetResponse(asset: Record<string, unknown>): Asset {
        const assetDetailsRaw = asset["asset_details"];
        const normalizedAssetDetails: { key: string; value: string }[] = Array.isArray(assetDetailsRaw)
            ? (assetDetailsRaw as { key: string; value: string }[]).filter(
                  (d) => typeof d.key === "string" && typeof d.value === "string"
              )
            : assetDetailsRaw !== null && typeof assetDetailsRaw === "object"
              ? Object.entries(assetDetailsRaw as Record<string, string>)
                    .filter(([, value]) => typeof value === "string")
                    .map(([key, value]) => ({ key, value }))
              : [];

        const getStringValue = (...keys: string[]): string | undefined => {
            for (const key of keys) {
                const value = toAssetStringValue(asset[key]);
                if (value !== undefined) {
                    return value;
                }
                const nestedValue = toAssetStringValue(
                    normalizedAssetDetails.find((detail) => detail.key === key)?.value
                );
                if (nestedValue !== undefined) {
                    return nestedValue;
                }
            }
            return undefined;
        };

        const getRequiredStringValue = (key: string): string => {
            const value = getStringValue(key);
            if (value === undefined) {
                throw new Error(`Asset response is missing required field: ${key}`);
            }
            return value;
        };

        const mappedFields: Record<string, string | undefined> = {};
        for (const pathKey of Object.values(AssetPathKey)) {
            const sourceKeys = AssetService.ASSET_PATH_TO_SOURCE_FIELDS.get(pathKey) ?? [];
            mappedFields[pathKey] = getStringValue(...sourceKeys, pathKey);
        }
        for (const [key, value] of Object.entries(asset)) {
            if (!(key in mappedFields) && isAssetScalarValue(value)) {
                mappedFields[key] = value.toString();
            }
        }

        return {
            uuid: getRequiredStringValue("uuid"),
            assetId: getRequiredStringValue("asset_id"),
            assetType: mappedFields[AssetPathKey.ASSET_TYPE],
            serialNumber: mappedFields[AssetPathKey.SERIAL_NUMBER],
            riskLevel: mappedFields[AssetPathKey.RISK_LEVEL],
            location: mappedFields[AssetPathKey.LOCATION],
            assignedTo: mappedFields[AssetPathKey.ASSIGNED_TO],
            model: mappedFields[AssetPathKey.MODEL],
            manufacturer: mappedFields[AssetPathKey.MANUFACTURER],
            warrantyStatus: mappedFields[AssetPathKey.WARRANTY_STATUS],
            lastErasureDate: mappedFields[AssetPathKey.LAST_ERASURE_DATE],
            dispositionGroup: mappedFields[AssetPathKey.DISPOSITION_GROUP],
            inventoryId: mappedFields[AssetPathKey.INVENTORY_ID],
            deviceType: mappedFields[AssetPathKey.DEVICE_TYPE],
            assetTag: mappedFields[AssetPathKey.ASSET_TAG],
            ...mappedFields,
            assetDetails: normalizedAssetDetails,
            assetState: mappedFields[AssetPathKey.ASSET_STATE],
            detailsTruncated: asset["details_truncated"] === true,
            isTracked: asset["is_tracked"] === true,
            isDataBearing: asset["is_data_bearing"] === true,
            groupUuids: asset["group_uuids"] as string[] | undefined,
            parentUuid: typeof asset["parent_uuid"] === "string" ? asset["parent_uuid"] : undefined,
            hasChildren: asset["has_children"] === true,
            hasHiddenChildren: asset["has_hidden_children"] === true,
            hasErasureReport: asset["has_erasure_reports"] === true,
            hasSuccessfulErasure: asset["has_successful_erasure"] === true,
            lockState: typeof asset["lock_state"] === "string" ? asset["lock_state"] : undefined,
            potentialUpliftRules: Array.isArray(asset["uplift_rules"])
                ? (asset["uplift_rules"] as UpliftRuleDto[]).map(toUpliftRule)
                : undefined,
            appliedUpliftRules: Array.isArray(asset["applied_uplift_rules"])
                ? (asset["applied_uplift_rules"] as UpliftRuleDto[]).map(toUpliftRule)
                : undefined,
        };
    }

    private static defaultColumns: AssetColumn[] = [
        { key: "assetId", visible: true, apiPath: "asset_id" },
        { key: "assetType", visible: true, apiPath: "asset_type" },
        { key: "assignedTo", visible: true, apiPath: "assigned_to" },
        { key: "serialNumber", visible: true, apiPath: "serial_number" },
        { key: "assetState", visible: true, apiPath: "asset_state" },
        { key: "riskLevel", visible: true, apiPath: "risk_level" },
        { key: "location", visible: true, apiPath: "location" },
        { key: "model", visible: false, apiPath: "model" },
        { key: "manufacturer", visible: false, apiPath: "manufacturer" },
        { key: "warrantyStatus", visible: false, apiPath: "warranty_status" },
        {
            key: "lastErasureDate",
            visible: false,
            apiPath: "last_erasure_date",
        },
        {
            key: "dispositionGroup",
            visible: false,
            apiPath: "disposition_group",
        },
        { key: "inventoryId", visible: false, apiPath: "inventory_id" },
        { key: "deviceType", visible: false, apiPath: "device_type" },
        { key: "assetTag", visible: false, apiPath: "asset_tag" },
    ];

    private static toTrackingSummary(dto: TrackingSummaryDto): TrackingSummary {
        return {
            trackedCount: dto.tracked_count,
            nonDataBearingCount: dto.non_data_bearing_count,
            trackingLimit: dto.tracking_limit,
            allDataBearingCount: dto.all_data_bearing_count,
        };
    }

    private static toAssetBreakdown(deviceType: string, dto: AssetBreakdownDto): AssetDeviceBreakdown {
        return {
            deviceType,
            count: dto.count,
            hasDataBearing: dto.has_data_bearing,
            hasNonDataBearing: dto.has_non_data_bearing,
        };
    }

    private static toAssetOverview(dto: AssetOverviewDto): AssetOverview {
        return {
            totalAssets: dto.total_assets,
            trackedDataBearingAssets: dto.tracked_data_bearing_assets,
            nonDataBearingAssets: dto.non_data_bearing_assets,
            breakdown: Object.entries(dto.breakdown).map(([deviceType, breakdown]) =>
                AssetService.toAssetBreakdown(deviceType, breakdown)
            ),
        };
    }

    public searchAssets(request: AssetSearchRequest, abortController?: AbortController): Promise<AssetSearchResponse> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, AssetSearchResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/view",
                "POST",
                toAssetSearchRequestDto(request),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((response) => this.toAssetSearchResponse(response));
    }

    public exportAssetsBySearchRequest(
        request: AssetSearchRequest,
        manifestCustomViewUuid?: string,
        abortController?: AbortController
    ): Promise<void> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto & { manifest_custom_view_uuid?: string }, Response>(
                ASSET_MANAGEMENT_PATH + "assets/export",
                "POST",
                { ...toAssetSearchRequestDto(request), manifest_custom_view_uuid: manifestCustomViewUuid },
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: false,
                    apiType: ApiType.OLIVER,
                    emptyResponse: true,
                }
            )
            .then(() => undefined);
    }

    public fetchTrackingSummary(abortController?: AbortController): Promise<TrackingSummary> {
        return apiGatewayService
            .invokeApi<null, TrackingSummaryDto>(ASSET_MANAGEMENT_PATH + "assets/summary", "GET", null, {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((dto) => AssetService.toTrackingSummary(dto));
    }

    public fetchRiskSummary(body?: AssetSearchRequest, abortController?: AbortController): Promise<RiskSummary> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, RiskSummaryDto>(
                ASSET_MANAGEMENT_PATH + "assets/risk-summary",
                "POST",
                toAssetSearchRequestDto(body ?? { paths: [] }),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => ({
                totalLow: dto.total_low,
                totalMedium: dto.total_medium,
                totalHigh: dto.total_high,
                total: dto.total,
            }));
    }

    private toAssetSearchResponse(response: AssetSearchResponseDto): AssetSearchResponse {
        return {
            assets: (response.assets ?? []).map((asset) => this.mapAssetResponse(asset)),
            cursor: response.cursor,
            currentPage: response.current_page,
            totalPages: response.total_pages,
            totalAssets: response.total_assets,
            trackingSummary: response.tracking_summary
                ? AssetService.toTrackingSummary(response.tracking_summary)
                : undefined,
            assetTypes: response.asset_types ?? [],
        };
    }

    public fetchLockDistributions(
        body: AssetDistributionRequest,
        abortController?: AbortController
    ): Promise<LockDistribution[]> {
        return apiGatewayService
            .invokeApi<AssetDistributionRequestDto, LockDistributionResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/lock-distributions",
                "POST",
                toAssetDistributionRequestDto(body),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => dto.distributions);
    }

    public fetchRiskDistributions(
        body: AssetDistributionRequest,
        abortController?: AbortController
    ): Promise<AssetSearchResponse> {
        return apiGatewayService
            .invokeApi<AssetDistributionRequestDto, AssetSearchResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/risk-distributions",
                "POST",
                toAssetDistributionRequestDto(body),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((response) => this.toAssetSearchResponse(response));
    }

    public fetchAssetOverview(request: AssetSearchRequest, abortController?: AbortController): Promise<AssetOverview> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, AssetOverviewDto>(
                ASSET_MANAGEMENT_PATH + "assets/overview",
                "POST",
                toAssetSearchRequestDto(request),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => AssetService.toAssetOverview(dto));
    }

    public fetchAssetByUuid(assetUuid: string, abortController?: AbortController): Promise<Asset> {
        return apiGatewayService
            .invokeApi<null, Record<string, unknown>>(
                ASSET_MANAGEMENT_PATH + "assets/" + encodeURIComponent(assetUuid),
                "GET",
                null,
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((response) => this.mapAssetResponse(response));
    }

    static getAvailableColumns(): AssetColumn[] {
        return [...this.defaultColumns];
    }

    static getRiskLevels(): string[] {
        return [RiskLevel.LOW, RiskLevel.MEDIUM, RiskLevel.HIGH];
    }

    public uploadAssets(csv: File, abortController?: AbortController, groupUuid?: string): Promise<void> {
        const formData = new FormData();
        formData.append("file", csv);

        const queryParams = new URLSearchParams();
        if (groupUuid) {
            queryParams.set("group_uuid", groupUuid);
        }
        const queryString = queryParams.toString();
        const uploadPath = ASSET_MANAGEMENT_PATH + "assets/upload" + (queryString ? "?" + queryString : "");

        return apiGatewayService.invokeApi(uploadPath, "POST", formData, {
            abortSignal: abortController?.signal,
            jsonResponse: false,
            apiType: ApiType.OLIVER,
        });
    }

    public createAsset(asset: Assets, abortController: AbortController, groupUuid?: string): Promise<void> {
        const queryParams = new URLSearchParams();
        if (groupUuid) {
            queryParams.set("group_uuid", groupUuid);
        }
        const queryString = queryParams.toString();
        const createPath = ASSET_MANAGEMENT_PATH + "assets/create" + (queryString ? "?" + queryString : "");

        return apiGatewayService.invokeApi(createPath, "POST", toCreateAsset(asset), {
            abortSignal: abortController.signal,
            jsonResponse: true,
            apiType: ApiType.OLIVER,
        });
    }

    public fetchExistingKeys(abortController: AbortController): Promise<AssetKeys> {
        return apiGatewayService.invokeApi(ASSET_MANAGEMENT_PATH + "assets/keys", "GET", undefined, {
            abortSignal: abortController.signal,
            jsonResponse: true,
            apiType: ApiType.OLIVER,
        });
    }

    public fetchAssetExportJob(jobUuid: string, abortController?: AbortController): Promise<AssetExportJobDetail> {
        return apiGatewayService
            .invokeApi<null, AssetExportJobDetailDto>(
                "/api/v1/asset-export-jobs/" + encodeURIComponent(jobUuid),
                "GET",
                null,
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.ARTHUR,
                }
            )
            .then((dto) => toAssetExportJobDetail(dto));
    }

    public exportAssets(
        filters?: { name: string; value: string; operator: string }[],
        search?: string,
        paths?: string[],
        abortController?: AbortController,
        manifestCustomViewUuid?: string,
        sendEmail?: boolean,
        emailRequest?: ExportEmailRequestDto,
        groupUuid?: string
    ): Promise<Blob> {
        const queryParams = new URLSearchParams();
        if (sendEmail !== undefined) {
            queryParams.set("email", String(sendEmail));
        }
        const queryString = queryParams.toString();
        const exportPath = ASSET_MANAGEMENT_PATH + "assets/export" + (queryString ? "?" + queryString : "");

        return apiGatewayService
            .invokeApi<unknown, Response>(
                exportPath,
                "POST",
                {
                    filters,
                    search,
                    paths,
                    manifest_custom_view_uuid: manifestCustomViewUuid,
                    email: emailRequest,
                    group_uuid: groupUuid,
                },
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: false,
                    apiType: ApiType.OLIVER,
                    emptyResponse: true,
                }
            )
            .then((response) => response.blob());
    }

    public getAvailableColumns(): AssetColumn[] {
        return AssetService.getAvailableColumns();
    }

    public getRiskLevels(): string[] {
        return AssetService.getRiskLevels();
    }

    public deleteAsset(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            ASSET_MANAGEMENT_PATH + "assets/" + encodeURIComponent(uuid),
            "DELETE",
            null,
            {
                abortSignal: abortController.signal,
                emptyResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
    }

    public bulkDeleteAssets(assetUuids: string[], abortController: AbortController): Promise<BulkDeleteAssetsResponse> {
        return apiGatewayService
            .invokeApi<BulkDeleteAssetsRequest, BulkDeleteAssetsResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/delete",
                "POST",
                { asset_uuids: assetUuids },
                {
                    abortSignal: abortController.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((response) => ({
                failedAssetUuids: response.failed_asset_uuids,
            }));
    }

    public editAsset(
        uuid: string,
        originalAsset: Assets,
        untrimmedUpdatedAsset: Assets,
        abortController: AbortController
    ): Promise<void> {
        const updatedAsset = trimAssets(untrimmedUpdatedAsset);
        const parentAssetIdDetail = (updatedAsset.assetDetails ?? []).find(
            (detail) => detail.key === PARENT_ASSET_ID_KEY
        );
        return apiGatewayService.invokeApi(
            ASSET_MANAGEMENT_PATH + "assets/" + encodeURIComponent(uuid),
            "POST",
            {
                details: computeDetailsDiff(toEditAssets(originalAsset), toEditAssets(updatedAsset)),
                parent_asset_id: parentAssetIdDetail?.value,
            },

            {
                abortSignal: abortController.signal,
                emptyResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
    }
    public fetchAssetEvents(assetUuid: string, abortController: AbortController): Promise<AssetEventResponse> {
        return apiGatewayService
            .invokeApi(ASSET_MANAGEMENT_PATH + "assets/" + assetUuid + "/history", "GET", null, {
                abortSignal: abortController.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((response: AssetEventResponseDto) => {
                return toAssetEventsResponse(response);
            });
    }

    async linkReportToAsset(
        assetUuid: string,
        reportUuid: string,
        presignedUrl: string,
        erasureStatus: string,
        reportDate: string,
        abortController?: AbortController
    ): Promise<void> {
        await apiGatewayService.invokeApi(
            ASSET_MANAGEMENT_PATH + "assets/" + assetUuid + "/upload",
            "POST",
            {
                report_uuid: reportUuid,
                presigned_url: presignedUrl,
                erasure_status: erasureStatus,
                report_date: reportDate,
            },
            { apiType: ApiType.OLIVER, abortSignal: abortController?.signal, emptyResponse: true }
        );
    }

    public fetchAssetErasureHistory(
        assetUuid: string,
        abortController: AbortController
    ): Promise<AssetErasureHistory[]> {
        return apiGatewayService
            .invokeApi(ASSET_MANAGEMENT_PATH + "assets/" + assetUuid + "/erasure-history", "GET", null, {
                abortSignal: abortController.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((response: AssetErasureHistoryResponseDto) => {
                return response.history.map((each) => toAssetErasureHistory(each));
            });
    }

    public registerAssetsFromReports(reportUuids: string[]): Promise<void> {
        return apiGatewayService.invokeApi(
            PATH_API_V1_ASSETS_REGISTRATION,
            "POST",
            { reportUuids: reportUuids },
            { apiType: ApiType.OLIVER, refreshSession: true, emptyResponse: true }
        );
    }

    async fetchAssetRegistrationJob(
        jobUuid: string,
        abortController?: AbortController
    ): Promise<AssetRegistrationJobDetail> {
        const dto = await apiGatewayService.invokeApi<null, AssetRegistrationJobDetailDto>(
            "/v1/api/reports/register-assets/" + encodeURIComponent(jobUuid),
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
        return toAssetRegistrationJobDetail(dto);
    }

    public bulkSetValueRecoveryAction(
        action: GradingAction,
        assetUuids: string[],
        abortController: AbortController
    ): Promise<BulkValueRecoveryActionResponse> {
        return apiGatewayService
            .invokeApi<BulkValueRecoveryActionRequest, BulkValueRecoveryActionResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/value-recovery-actions",
                "POST",
                { action, assets: assetUuids },
                {
                    abortSignal: abortController.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((response) => ({
                failedAssetUuids: response.failed_asset_uuids,
            }));
    }

    public editAssetWithGivenDetails(
        uuid: string,
        assetDetailDifference: AssetDetailDifference,
        abortController: AbortController
    ): Promise<void> {
        return apiGatewayService.invokeApi(
            ASSET_MANAGEMENT_PATH + "assets/" + encodeURIComponent(uuid),
            "POST",
            {
                details: [assetDetailDifference],
            },

            {
                abortSignal: abortController.signal,
                emptyResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
    }
}

export const assetService = new AssetService();

export const DEFAULT_COLUMN_KEYS = assetService
    .getAvailableColumns()
    .filter((column) => column.visible)
    .map((column) => column.key);
